// The in-memory draft registry for the global chat. A draft belongs to ONE private-chat context: either
// an opaque local context that has not minted a Session yet, or a platform-owned Session after the first
// send / a history switch. The registry is client view-state only — never persisted, never sent, and never
// used as authority. Its one job is to stop unsent words leaking across conversations or disappearing when
// the dock closes.

import { boundRetainedDraft } from './draft-text-limit.ts';

/** Bound retained drafts so repeated conversation switches cannot grow tab memory without limit. */
export const MAX_RETAINED_CHAT_DRAFTS = 20;

/** Opaque UI context keys. Session ids are namespaced; local ids are monotonic shell-only counters. */
export type ChatDraftContextKey = `local:${number}` | `session:${string}`;

interface ChatDraftEntry {
  readonly key: ChatDraftContextKey;
  readonly draft: string;
}

export interface ChatDraftRegistry {
  readonly activeKey: ChatDraftContextKey;
  /** Oldest touched first. Only non-empty drafts need an entry. */
  readonly entries: readonly ChatDraftEntry[];
}

export function localChatDraftKey(sequence: number): ChatDraftContextKey {
  return `local:${sequence}`;
}

/** Reject an invalid/empty Session identity rather than aliasing it to a shared fallback context. */
export function sessionChatDraftKey(sessionId: string): ChatDraftContextKey | null {
  const clean = sessionId.trim();
  return clean.length > 0 ? `session:${clean}` : null;
}

export function createChatDraftRegistry(initialKey: ChatDraftContextKey): ChatDraftRegistry {
  return { activeKey: initialKey, entries: [] };
}

export function activeChatDraft(registry: ChatDraftRegistry): string {
  return registry.entries.find((entry) => entry.key === registry.activeKey)?.draft ?? '';
}

/** Keep the active entry and the 19 most-recent other drafts. */
function boundEntries(
  entries: readonly ChatDraftEntry[],
  activeKey: ChatDraftContextKey,
): readonly ChatDraftEntry[] {
  const bounded = [...entries];
  while (bounded.length > MAX_RETAINED_CHAT_DRAFTS) {
    const oldestInactive = bounded.findIndex((entry) => entry.key !== activeKey);
    if (oldestInactive < 0) break;
    bounded.splice(oldestInactive, 1);
  }
  return bounded;
}

function writeDraft(
  registry: ChatDraftRegistry,
  key: ChatDraftContextKey,
  draft: string,
): ChatDraftRegistry {
  const withoutKey = registry.entries.filter((entry) => entry.key !== key);
  const boundedDraft = boundRetainedDraft(draft);
  const entries =
    boundedDraft.length > 0 ? [...withoutKey, { key, draft: boundedDraft }] : withoutKey;
  return { activeKey: key, entries: boundEntries(entries, key) };
}

/** Update only the active conversation's draft. No caller can accidentally write another context. */
export function writeActiveChatDraft(
  registry: ChatDraftRegistry,
  draft: string,
): ChatDraftRegistry {
  return writeDraft(registry, registry.activeKey, draft);
}

/** Switch contexts and restore that context's retained draft, or install an explicit fresh prefill. */
export function activateChatDraftContext(
  registry: ChatDraftRegistry,
  key: ChatDraftContextKey,
  prefill?: string,
): ChatDraftRegistry {
  if (prefill !== undefined) return writeDraft(registry, key, prefill);
  const retained = registry.entries.find((entry) => entry.key === key);
  if (retained === undefined) return { ...registry, activeKey: key };
  return writeDraft(registry, key, retained.draft);
}

/**
 * Rebind the active local context after the platform mints/resumes its Session. A retained Session draft
 * wins a collision: the local entry remains preserved under its opaque key rather than overwriting words
 * already belonging to that Session. In the ordinary first-send path both entries are empty.
 */
export function bindActiveChatDraftToSession(
  registry: ChatDraftRegistry,
  sessionId: string,
): ChatDraftRegistry {
  const targetKey = sessionChatDraftKey(sessionId);
  if (targetKey === null || targetKey === registry.activeKey) return registry;
  // Only a not-yet-minted local context may acquire a Session identity. If a controller resumes or opens
  // a different Session while another Session draft is active, SELECT the target context; never rename the
  // source entry and leak its words across the boundary.
  if (registry.activeKey.startsWith('session:')) {
    return activateChatDraftContext(registry, targetKey);
  }
  const source = registry.entries.find((entry) => entry.key === registry.activeKey);
  const target = registry.entries.find((entry) => entry.key === targetKey);
  if (target !== undefined) return activateChatDraftContext(registry, targetKey);

  const withoutSource = registry.entries.filter((entry) => entry.key !== registry.activeKey);
  const rebound =
    source === undefined ? withoutSource : [...withoutSource, { ...source, key: targetKey }];
  return { activeKey: targetKey, entries: boundEntries(rebound, targetKey) };
}
